export type McpClientId =
  | 'claude-desktop'
  | 'chatgpt'
  | 'grok'
  | 'claude-code'
  | 'cursor'
  | 'opencode'
  | 'vscode'
  | 'openai-codex'
  | 'manus'
  | 'windsurf'
  | 'cline'
  | 'goose'
  | 'generic'

/** How a client connects — drives the copy + the kind of artifact we show. */
type McpClientConnect = 'plugin' | 'connector' | 'cli' | 'config'

export type McpClientSetup = {
  id: McpClientId
  name: string
  /** Brand domain for the logo (Brandfetch CDN, Google-favicon fallback). */
  logoDomain: string
  /** One-line summary under the client name. */
  tagline: string
  /** Plugin/connector UI, a CLI command, or a config file. */
  connect: McpClientConnect
  /** Config file path (if the client is file-configured), else null. */
  configPath: string | null
  /** Syntax-highlight language for the copyable block. */
  language: 'bash' | 'json' | 'yaml' | 'toml'
  /** Ordered, do-this setup steps. */
  steps: ReadonlyArray<string>
  /** The copyable artifact for the live connection URL — the bare URL
   *  (plugin/connector), a command (cli), or a config block (config). */
  buildConfig: (url: string) => string
  /** Optional caveat surfaced as a small callout (plan gating, restart, …). */
  caveat?: string
}

const json = (obj: unknown): string => JSON.stringify(obj, null, 2)

export const MCP_CLIENTS: ReadonlyArray<McpClientSetup> = [
  {
    id: 'claude-desktop',
    name: 'Claude Desktop',
    logoDomain: 'claude.com',
    tagline: 'Works with Claude Desktop and claude.ai',
    connect: 'connector',
    configPath: null,
    language: 'bash',
    steps: [
      'Open Settings → Connectors.',
      'Click "Add custom connector".',
      'Paste the connection URL below.',
      'Click Connect, sign in with Brew, then choose one brand or all brands.',
    ],
    buildConfig: (url) => url,
  },
  {
    id: 'chatgpt',
    name: 'ChatGPT',
    logoDomain: 'openai.com',
    tagline: 'Works with the ChatGPT app and chatgpt.com',
    connect: 'plugin',
    configPath: null,
    language: 'bash',
    steps: [
      'Enable Developer mode under Settings → Security and login.',
      'Open Settings → Plugins, select +, and create Brew with the server URL below and OAuth authentication.',
      'Sign in to Brew, choose one brand or all brands, and approve the disclosed access.',
      'Start a new chat and enable @Brew from the Plugins menu.',
      'Call get_brew_capabilities to verify the current action catalog.',
    ],
    buildConfig: (url) => url,
    caveat:
      'If an older cached version reports an invalid schema, refresh its actions. After the corrected endpoint is deployed, remove and re-add Brew only if refresh does not work.',
  },
  {
    id: 'grok',
    name: 'Grok',
    logoDomain: 'x.ai',
    tagline: 'Works with grok.com and Grok CLI',
    connect: 'connector',
    configPath: null,
    language: 'bash',
    steps: [
      'Open grok.com/connectors and select New Connector → Custom.',
      'Name it Brew, paste the connection URL below, and start authentication.',
      'Sign in to Brew, choose one brand or all brands, and approve the access.',
      'Start a new Grok chat, enable Brew, and call get_brew_capabilities.',
    ],
    buildConfig: (url) => url,
    caveat:
      'Grok CLI: run `grok mcp add --transport http brew https://brew.new/api/mcp`, authenticate from `/mcps` if needed, then run `grok mcp doctor brew`.',
  },
  {
    id: 'claude-code',
    name: 'Claude Code',
    logoDomain: 'claude.com',
    tagline: 'Anthropic CLI — one command',
    connect: 'cli',
    configPath: '~/.claude.json',
    language: 'bash',
    steps: [
      'Run the command below in your terminal.',
      'Claude opens your browser to sign in with Brew and choose one brand or all brands.',
      'Start a session and run /mcp to confirm Brew is connected.',
    ],
    buildConfig: (url) => `claude mcp add --transport http brew "${url}"`,
  },
  {
    id: 'cursor',
    name: 'Cursor',
    logoDomain: 'cursor.com',
    tagline: 'Add to ~/.cursor/mcp.json',
    connect: 'config',
    configPath: '~/.cursor/mcp.json',
    language: 'json',
    steps: [
      'Open ~/.cursor/mcp.json (global) or .cursor/mcp.json (this project).',
      'Add the brew server below.',
      'Cursor opens your browser to sign in with Brew — then check Settings → MCP for a green dot.',
    ],
    buildConfig: (url) => json({ mcpServers: { brew: { url } } }),
  },
  {
    id: 'opencode',
    name: 'OpenCode',
    logoDomain: 'opencode.ai',
    tagline: 'Add to ~/.config/opencode/opencode.json',
    connect: 'config',
    configPath: '~/.config/opencode/opencode.json',
    language: 'json',
    steps: [
      'Open ~/.config/opencode/opencode.json.',
      'Add the brew server below.',
      'Run opencode mcp auth brew, then sign in with Brew and choose one brand or all brands.',
      'Run opencode mcp list to confirm Brew is connected.',
    ],
    buildConfig: (url) =>
      json({ mcp: { brew: { type: 'remote', url, enabled: true } } }),
  },
  {
    id: 'vscode',
    name: 'VS Code',
    logoDomain: 'code.visualstudio.com',
    tagline: 'Copilot agent mode — .vscode/mcp.json',
    connect: 'config',
    configPath: '.vscode/mcp.json',
    language: 'json',
    steps: [
      'Create .vscode/mcp.json with the block below.',
      'Click Start in the mcp.json gutter and sign in with Brew when prompted.',
      'Brew appears in the agent-mode tools picker.',
    ],
    buildConfig: (url) => json({ servers: { brew: { type: 'http', url } } }),
    caveat: 'Requires GitHub Copilot agent mode.',
  },
  {
    id: 'openai-codex',
    name: 'OpenAI Codex',
    logoDomain: 'openai.com',
    tagline: 'Add to ~/.codex/config.toml',
    connect: 'config',
    configPath: '~/.codex/config.toml',
    language: 'toml',
    steps: [
      'Open ~/.codex/config.toml.',
      'Add the [mcp_servers.brew] block below.',
      'Run codex mcp login brew, then sign in with Brew and choose one brand or all brands.',
      'Start Codex and use /mcp to confirm Brew is connected.',
    ],
    buildConfig: (url) => `[mcp_servers.brew]\nurl = "${url}"`,
  },
  {
    id: 'manus',
    name: 'Manus',
    logoDomain: 'manus.im',
    tagline: 'Add a custom MCP connector',
    connect: 'connector',
    configPath: null,
    language: 'bash',
    steps: [
      'Open Manus → Settings → MCP / Connectors.',
      'Add a custom MCP server with the connection URL below.',
      'Sign in with Brew and choose one brand or all brands.',
    ],
    buildConfig: (url) => url,
  },
  {
    id: 'windsurf',
    name: 'Windsurf',
    logoDomain: 'windsurf.com',
    tagline: 'Cascade — mcp_config.json',
    connect: 'config',
    configPath: '~/.codeium/windsurf/mcp_config.json',
    language: 'json',
    steps: [
      'Open Settings → Cascade → MCP Servers → View raw config.',
      'Add the remote-server block below.',
      'Click Refresh in the MCP panel and sign in with Brew when prompted.',
    ],
    buildConfig: (url) => json({ mcpServers: { brew: { serverUrl: url } } }),
    caveat:
      'Current Windsurf builds accept serverUrl or url for remote Streamable HTTP servers.',
  },
  {
    id: 'cline',
    name: 'Cline',
    logoDomain: 'cline.bot',
    tagline: 'MCP Servers → Remote',
    connect: 'config',
    configPath: 'cline_mcp_settings.json',
    language: 'json',
    steps: [
      'Open Cline → MCP Servers → Configure MCP Servers.',
      'Add the brew server below.',
      'Save; Cline connects to the remote server and signs in with Brew.',
    ],
    buildConfig: (url) =>
      json({ mcpServers: { brew: { url, disabled: false } } }),
  },
  {
    id: 'goose',
    name: 'Goose',
    logoDomain: 'block.xyz',
    tagline: 'goose configure → Remote Extension',
    connect: 'cli',
    configPath: '~/.config/goose/config.yaml',
    language: 'bash',
    steps: [
      'Run goose configure.',
      'Choose Add Extension → Remote Extension (Streamable HTTP).',
      'Name it brew, paste the connection URL, then sign in with Brew.',
    ],
    buildConfig: (url) =>
      `goose configure\n#  → Add Extension → Remote Extension (Streamable HTTP)\n#  name:     brew\n#  endpoint: ${url}`,
    caveat: 'Restart Goose after editing the config.',
  },
  {
    id: 'generic',
    name: 'Any MCP client',
    logoDomain: 'modelcontextprotocol.io',
    tagline: 'The canonical mcpServers block',
    connect: 'config',
    configPath: null,
    language: 'json',
    steps: [
      'Add Brew to your client’s mcpServers map with the url below.',
      'Reload or restart the client per its convention.',
      'Sign in with Brew (OAuth) on first connect and pick your brand; if your client lacks OAuth support, add an Authorization: Bearer <brew key> header instead.',
    ],
    buildConfig: (url) => json({ mcpServers: { brew: { url } } }),
  },
]

export function getMcpClient(id: McpClientId): McpClientSetup {
  const client = MCP_CLIENTS.find((c) => c.id === id)
  if (!client) {
    throw new Error(`Unknown MCP client: ${id}`)
  }
  return client
}
